import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type LocalFilterSection } from '@/storybook/components/FilterSetPreview/FilterSetPreview';
import { AppliedFilterSet } from '@/utils/filter/appliedFilterSetStorage';
import {
  buildGlobalChips,
  buildLocalSections,
  parseDateRange,
} from '@/components/filter/SaveFiltersDialog/SaveFiltersDialog.utils';
import FilterPreviewModal from '@/components/filter/FilterPreviewModal/FilterPreviewModal';

interface AppliedFilterSetDialogProps {
  isOpen: boolean;
  appliedSet: AppliedFilterSet | null;
  onClose: () => void;
}

const EMPTY_LOCAL_SECTIONS: LocalFilterSection[] = [];

const AppliedFilterSetDialog: FC<AppliedFilterSetDialogProps> = ({ isOpen, appliedSet, onClose }) => {
  const { t } = useTranslation();

  const parsedData = useMemo(() => {
    if (!appliedSet) return null;

    const { localFilters = {} } = appliedSet;
    const hasLocalFilters = Object.keys(localFilters).length > 0;

    return {
      dateRangeText: parseDateRange(appliedSet.globalFilters, t),
      globalFilterChips: buildGlobalChips(appliedSet.globalFilters, t),
      localFilterSections: hasLocalFilters ? buildLocalSections(localFilters, t) : EMPTY_LOCAL_SECTIONS,
    };
  }, [appliedSet, t]);

  if (!appliedSet) return null;

  return (
    <FilterPreviewModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('global.filters.manageSavedFilters.filterDetailsTitle')} ${appliedSet.filterName ?? appliedSet.userFilterId}`}
      data={parsedData}
      data-testid="applied-filter-set-dialog"
    />
  );
};

export default AppliedFilterSetDialog;
